/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import isArray from 'lodash.isarray';
import isUndefined from 'lodash.isundefined';
import moment from 'moment';
import {
  deleteLocalStorage,
  getLocalStorage,
  setLocalStorage
} from '@ion/core/src/capabilities/storage';
import {
  getLanguage,
  getLanguageKey
} from '@ion/core/src/locale/locale';
import {error, info} from '@ion/core/src/logger/logger';
import {
  ExtendedAttributesPreferences,
  IUserPreferences,
  PreferencesStore
} from '@ion/core/src/preferences/types';
import {
  saveUserExtendedAttributes
} from '@ion/core/src/services/Connect';
import {
  parseExtendedAttributes
} from '@ion/core/src/services/ConnectUtils';
import GroupProperties, {
  getDateFormat,
  getTimeFormat as getGroupTimeFormat,
  getUrgentNotificationsEnabled
} from '@ion/core/src/services/GroupProperties';
import {
  Language,
  Preference
} from '@ion/core/src/types/preferences';
import {
  DEFAULT_TIME_FORMAT,
  getDefaultDateFormat
} from '@ion/core/src/utils/DateUtil';
import {
  safeJSONParse,
  safeJSONStringify
} from '@ion/core/src/utils/JSONUtils';
import {
  clone
} from '@ion/core/src/utils/ObjectUtil';
import {getEnvironment} from '../environment/environment';
import {
  ExtendedAttribute,
  User
} from '../types/entities';

/**
 * The Preferences Module provides the abstraction which is to be extended
 * by concrete Preference implementations, or via composition or delegation.
 *
 * Preferences are stored within a "preferences" key of the given entity's
 * extended attributes value property.
 *
 * ```typescript
 *
 *   import UserPreferences from '@ion/core/src/preferences/UserPreferences';
 *
 *   // initialize user preferences ...
 *   const initialized = await UserPreferences.initialize(userId);
 *
 *   // determine if user preferences have been initialized ...
 *   const initialized = UserPreferences.isInitialized();
 *
 *   // retrieve a user preference ...
 *   const bookmarks = UserPreferences.get('tracking.map.bookmarks');
 *
 *   // set a user preference ...
 *   UserPreferences.set('tracking.map.bookmarks', bookmarks);
 *
 *   // set a user preference and immediately save to the underlying service ...
 *   const result = await UserPreferences.set('something', value, true);
 *
 *   // remove a user preference  ...
 *   UserPreferences.remove('something');
 *
 *   // remove a user preference and immediately save to the underlying service ...
 *   const result = await UserPreferences.remove('something', true);
 *
 *   // save all preferences ...
 *   const result = await UserPreferences.save();
 *
 * ```
 *
 * @class UserPreferences
 */
class UserPreferences implements IUserPreferences {
  /**
   * @private
   */
  private _store: PreferencesStore;

  /**
   * @private
   */
  private _initialized: boolean;

  /**
   * @private
   */
  private _userId: number;

  /**
   * @private
   */
  private _namespace: string;

  /**
   * @private
   */
  private _readOnly: boolean;

  /**
   * @private
   *
   * Allows for automated detecting changes of User Preferences which have not
   * been saved; thus allowing for implementing Auto Change features which can
   * determine if changes should be saved based on the changed state. By default,
   * this feature is opt-in, therefore `enableChangeDetection` is set to `false`.
   * To enabled automatic change detection, simply set `enableChangeDetection`
   * to `true` during initialization.
   */
  private _enableChangeDetection: boolean;

  /**
   * @private
   *
   * Provides a serialized reference to the store when preferences are initially
   * loaded and when they are saved. This state allows for determining if there
   * are changes to the current in-memory store for use by related Auto Save APIs.
   *
   * By default, this the store will only be serialized for change detection use
   * if the `enableChangeDetection` argument is set to `true` during initialization,
   * otherwise, it will intentionally remain `undefined`.
   */
  private _serializedStore: string;

  /**
   * @private
   */
  private _notificationPreferencesInitialized: boolean;

  /**
   * @private
   *
   * Incremented on every `set`/`remove` mutation of `_store`. Used by `save` to
   * detect whether the in-memory store changed while an external save request
   * was in flight, so a stale response can never overwrite a newer local change.
   */
  private _version = 0;

  /**
   * Provides the single point of entry for initializing the Preferences Module.
   *
   * The id argument is mandatory, and is used to specify the id of the user
   * for which the extended attributes are to be retrieved.
   *
   * The optional value argument can be used to bootstrap the Preferences, which
   * allows for bypassing the loading of the extended attributes externally and
   * initializing the module based on the provided object.
   *
   * ```typescript
   *
   *   import {UserPreferences} from '@ion/core/src/preferences';
   *
   *   // initialize via retrieving extended attributes ...
   *   UserPreferences.initialize(id).then(...);
   *
   *   // initialize via the provided extended attributes ...
   *   const value = JSON.parse(extendedAttributes.value)
   *   Preferences.initialize(id, value).then(...);
   *
   * ```
   *
   * @method  initialize
   * @param   {number} userId The Id of the User for which preferences are to be
   *          retrieved.
   * @param   {object} value An optional initialization value which can be used to
   *          bypass the loading of extended attributes externally. When this argument
   *          is provided, the module will be initialized against this object, and the
   *          extended attributes will not be loaded. This parameter is intended for
   *          cases where the entity's extended attributes have already been retrieved
   *          and are readily available.
   * @param   {string} namespace An optional namespace with which all preferences will
   *          be referenced and persisted.
   * @param   readOnly When `true`, indicates that all preferences are to be persisted
   *          locally via `localStorage` rather than externally via `extendedAttributes.
   *          By default, this parameter is `false`, and is provided for use-cases where
   *          a user is a Read-Only user and thus will not have the necessary permissions
   *          to write to the Platform.
   * @param   enableChangeDetection When `true`, indicates that changes to user preferences
   *          will be tracked, thus allowing for implementing Auto Change features which
   *          can determine if changes should be saved based on the changed state. By
   *          default, this is an opt-in feature, therefore `enableChangeDetection` is
   *          set to `false`. To enabled, simply set to `true`.
   * @returns {Promise} A Promise which resolves to true once the entity's preferences
   *          have been retrieved (via CTC extended attributes). In the case where the
   *          bootstrap value is provided, the promise will immediately resolve.
   */
  // eslint-disable-next-line max-params
  initialize = async(
    userId: number, value?: PreferencesStore, namespace = '', readOnly = false, enableChangeDetection = false): Promise<boolean> => {
    if (!this._initialized) {
      this._userId    = userId;
      this._namespace = namespace;
      this._readOnly  = readOnly;
      this._enableChangeDetection  = enableChangeDetection;
      if (!value || readOnly) {
        if (readOnly) {
          value = await this.initializeLocalPreferences(userId);
        } else if (!value) {
          value = await this.initializeExternalPreferences(userId);
        }
      } else {
        info(`[User Preferences]: Externally persisted User Preferences provided for user [${userId}].`);
      }
      this._store = value || {preferences: {}};
      if (!this._store.preferences) {
        this._store.preferences = {};
      }
      if (namespace && !this._store?.preferences[namespace]) {
        this._store.preferences[namespace] = {};
      }
      if (enableChangeDetection) {
        this._serializedStore = safeJSONStringify(this._store);
      }
      this._initialized = true;
      return Promise.resolve(true);
    }
  };

  /**
   * Manages initializing User Preferences from `extendedAttributes` (default behavior).
   *
   * This method is only to be invoked via the `initialize` method during initialization,
   * and handles initializing the given users user preferences store from Platform Services.
   * If the user currently does not have preferences persisted to localStorage, the
   * default preferences store will be initialized, added to localStorage, and returned.
   *
   * @param   userId The id of the authenticated user.
   * @returns A reference to the externally persisted preferences for the authenticated
   *          User `id`.
   */
  private initializeExternalPreferences = async(userId: number): Promise<PreferencesStore> => {
    let value: PreferencesStore;
    const defaultStore = {preferences: {}};
    info(`[User Preferences]: Externally persisted User Preferences do not currently exist for user [${userId}], initializing for the first time ...`);
    try {
      const attrs: ExtendedAttribute = await saveUserExtendedAttributes(this._userId, defaultStore, 'post');
      value = (attrs.value || attrs) as PreferencesStore;
      info(`[User Preferences]: Externally persisted User Preferences have been successfully initialized for [${userId}].`);
    } catch (err) {
      error(`[User Preferences]: Error initializing User Preferences from external persisted layer for user [${userId}].`);
    }
    return Promise.resolve(value || defaultStore);
  };

  /**
   * Manages initializing User Preferences from `localStorage` (optional behavior), when
   * in Read-Only mode.
   *
   * This method is only to be invoked via the `initialize` method during initialization,
   * and handles initializing the given users user preferences store from localStorage.
   * If the user currently does not have preferences persisted to localStorage, the
   * default preferences store will be initialized, added to localStorage, and returned.
   *
   * @param   userId The id of the authenticated user.
   * @returns A reference to the locally persisted preferences for the authenticated
   *          User `id`.
   */
  private initializeLocalPreferences = async(userId: number): Promise<PreferencesStore> => {
    const id = userId.toString();
    let store = getLocalStorage(id);
    if (!store) {
      info(`[User Preferences]: Locally persisted User Preferences do not currently exist for Read-Only User [${id}], initializing for the first time ...`);
      store = {preferences: {}};
      setLocalStorage(id, store);
    } else {
      info(`[User Preferences]: Locally persisted User Preferences have been successfully retrieved for Read-Only User [${id}].`);
    }
    return Promise.resolve(store);
  };

  /**
   * Provides a utility which allows for initializing user preferences from the
   * authenticated `User` entity, while also returning the `lastModifiedOn` date
   * of the user's preferences.
   *
   * This method is primarily to be used for obtaining  `UserPreferences` from
   * the `User` entity for use when invoking `UserPreferences.initialize();` in
   * order to provide the parsed preferences to the `value` argument while also
   * obtaining the `lastModifiedOn` date which can be used auto-save functionality,
   * specifically, determining if the user preferences have not been saved prior
   * to their session having expired, etc..
   *
   */
  fromExtendedAttributes(user: User): ExtendedAttributesPreferences {
    const {value, lastModifiedOn} = parseExtendedAttributes(user) || {};
    return {
      preferences : value ? safeJSONParse(value) : {preferences: {}},
      lastModifiedOn
    };
  };

  /**
   * Provides a means for determining if the Preferences module has been
   * initialized (i.e. Preferences.initialize has been invoked).
   *
   * ```typescript
   *
   *   import {UserPreferences} from '@ion/core/src/preferences';
   *
   *   // determine if preferences have been initialized before setting ...
   *   if (UserPreferences.isInitialized()) {
   *     UserPreferences.set('tracking.map.bookmarks', bookmarks);
   *   }
   *
   * ```
   *
   * @method  isInitialized
   * @returns {boolean} true if Preferences have been initialized, otherwise,
   *          returns false. Initialization is based on the initialize entry
   *          point method being invoked and retrieval of preferences being
   *          resolved.
   */
  isInitialized = () => this._initialized;

  /**
   * Provides an explicit save mechanism which can be to save all current
   * preferences to the underlying data store (i.e. extended attributes).
   * This method can also be used implicitly via the 'auto-save' argument
   * of the set method.
   *
   * ```typescript
   *
   *   import {UserPreferences} from '@ion/core/src/preferences';
   *
   *   const result = await UserPreferences.save();
   *
   * ```
   *
   * @method  save
   * @returns {Promise} A Promise which resolves once the preferences have
   *          been saved (via CTC extended attributes).
   */
  save = async(): Promise<boolean> => {
    if (this._initialized) {
      let result;
      const versionAtSave = this._version;
      if (!this._readOnly) {
        try {
          result = await saveUserExtendedAttributes(this._userId, this._store);
          info('[User Preferences]: Successfully Saved User Preferences to external persistence layer.');
        } catch (err) {
          error('[User Preferences]: An Error occurred while attempting to Save User Preferences to external Persistence Layer.');
        }
      } else {
        setLocalStorage(this._userId.toString(), this._store);
        info('[User Preferences]: Successfully Saved User Preferences to Local Persistence Layer.');
      }
      // only adopt the server's echoed value if nothing has mutated the store
      // since this save started - otherwise a slower, earlier request would
      // clobber a newer local change with its now-stale response ...
      if (this._version === versionAtSave) {
        this._store = result?.value || this._store;
        if (this._enableChangeDetection) {
          this._serializedStore = safeJSONStringify(this._store);
        }
      }
      return Promise.resolve(Boolean(result?.value));
    }
  };

  /**
   * The transfer method allows for lifting up preferences, or pulling down preference
   * within the preferences store. This method essentially affords the ability to move
   * preferences within the store, and is generally only required to facilitate migrations
   * within the system.
   *
   * ```typescript
   *
   *  import {UserPreferences} from '@ion/core/src/preferences';
   *
   *  // transfer a preference ...
   *  UserPreferences.transfer('ion-suite');
   *
   *  // transfer a preference and immediately save to the underlying service ...
   *  UserPreferences.transfer('ion-suite', {immediate: true}
   *
   *  ...
   *
   * ```
   *
   * @param path The path from which the preference is to be transferred
   *        to root level
   * @param options.remove When true, the property with path will be removed from
   *        the root preferences. Defaults to true.
   * @param options.override When true, indicates that if the preference exists in
   *        the new path, it will be overwritten. Defaults to true.
   * @param options.immediate When true, indicates that the preference should be
   *        removed from CTC service immediately. Defaults to false.
   */
  transfer = (path, {remove = true, override = true, immediate = false}) => {
    const origin = this.get(path);
    for (const key in origin) {
      if (isUndefined(this.get(key)) || override) {
        this.set(key, origin[key]);
      }
    }
    if (remove) {
      this.remove(path);
    }
    if (immediate) {
      this.save();
    }
  };

  /**
   * Returns the value for the given preference based on the provided lookup path.
   *
   * ```typescript
   *
   *   import {UserPreferences} from '@ion/core/src/preferences';
   *
   *   // get a preference ...
   *   const bookmarks = UserPreferences.get('tracking.map.bookmarks');
   *
   * ```
   *
   * @method  get
   * @param   {string} path The path to which the preference has been set in the form of
   *          <feature>.<context>.<name>. For example, 'pages.tracking.cvs.grid.columns'.
   * @param   {boolean} omitNamespace When true, indicates that the preference is to be
   *          referenced from the root of the preferences store, as opposed to the namespace
   *          within the store, which is the default behavior when the UserPreferences API
   *          is initialized against a namespace.
   * @returns {any} The value of the specific preference, or undefined if a value has not
   *          been previously set for the preference path.
   */
  get = (path: string | string[], omitNamespace = false) => {
    if (this._initialized) {
      const preferences = this._resolve(omitNamespace);
      if (isArray(path)) {
        const results = {};
        (path as string[]).forEach(preference => {
          results[preference] = preferences[preference];
        });
        return results;
      } else {
        return preferences[(path as string)];
      }
    } else {
      error(`[User Preferences]: Unable to reference '${path.toString()}' User Preference.`);
    }
  };

  /**
   * Sets the value for the given preference based on the provided lookup path.
   * The immediate argument can be passed a value of true to save all preferences
   * immediately after setting the preference.
   *
   * ```typescript
   *
   *   import {UserPreferences} from '@ion/core/src/preferences';
   *
   *   // set a preference ...
   *   UserPreferences.set('tracking.map.bookmarks', bookmarks);
   *
   *   // set a preference and immediately save to the underlying service ...
   *   const result = await UserPreferences.set('tracking.map.bookmarks', bookmarks, true);
   *
   * ```
   *
   * @method  set
   * @param   {string} path The path to which the preference is to be set in the form
   *          of <feature>.<context>.<name>.
   * @param   {any} val The value which is to be saved to the user preferences store.
   * @param   {boolean} immediate When true, indicates that the preference should be
   *          saved to CTC services immediately. By default saving of preferences to
   *          CTC Services is made explicitly via the save method. Generally, preferences
   *          need not be saved with each and every update. Use this argument only when a
   *          preference must be saved immediately depending on the context. Defaults to
   *          false.
   * @returns {Promise|undefined} If the immediate argument is true, returns a Promise
   *          which resolves once Preferences have been saved. Otherwise, returns
   *          undefined.
   */
  set = (path: string, val: any, immediate = false) => {
    if (this._initialized) {
      const preferences = this._resolve();
      preferences[path] = val;
      this._version++;
      info(`[User Preferences]: Added '${path}' User Preference.`);
      if (immediate) {
        return this.save();
      }
    } else {
      error(`[User Preferences]: Unable to set User Preferences for ${path}`);
    }
  };

  /**
   * Removes a previously set preference from the store. The immediate argument
   * can be passed as true updated the preferences immediately from the underlying
   * Service.
   *
   * ```typescript
   *
   *   import {UserPreferences} from '@ion/core/src/preferences';
   *
   *   // remove a preference ...
   *   UserPreferences.remove('tracking.maps.bookmarks');
   *
   *   // remove a preference and immediately save to the underlying service ...
   *   const result = await UserPreferences.remove('tracking.map.bookmarks', true);
   *
   * ```
   *
   * @method  set
   * @param   {string} path The path from which the preference is to be removed in
   *          the form of <feature>.<context>.<name>.
   * @param   {boolean} immediate When true, indicates that the preference should be
   *          removed from CTC services immediately. By default removal of preferences
   *          from CTC Services are made explicitly via the save method. Defaults to
   *          false.
   * @returns {Promise|undefined} If the immediate argument is true, returns a Promise
   *          which resolves once Preference has been removed. Otherwise, returns
   *          undefined.
   */
  remove = (path: string, immediate = false, omitNamespace = false) => {
    if (this._initialized) {
      const preferences = this._resolve(omitNamespace);
      delete preferences[path];
      this._version++;
      info(`[User Preferences]: Deleted '${path}' User Preference.`);
      if (immediate) {
        return this.save();
      }
    } else {
      error(`[User Preferences]: Unable to remove User Extended Attributes for ${path}`);
    }
  };

  /**
   * Allows for enabling automatic change detection. When enabled, this feature can
   * be used to determine if there have changes which haven't been persisted yet via
   * related Auto Save APIs.
   *
   * By default, the auto change detection feature is opt-in, thus when initializing
   * the User Preferences API, to enable auto change detection, `enableChangeDetection`
   * should be set to `true` by clients. In the case that change detection has not been
   * enabled during initialization, this method provides a means of enablement.
   *
   * If change detection was not enabled during initialization, the current state of
   * the user preferences store will be used from this point on to determine if there
   * has been changes when `UserPreferences.hasChanges();` is invoked.
   *
   * ```typescript
   *  if (!UserPreferences.hasChangeDetection()) {
   *    UserPreferences.enableChangeDetection();
   *    ...
   *  }
   * ```
   */
  enableChangeDetection() {
    this._enableChangeDetection = true;
    if (!this._serializedStore) {
      this._serializedStore = safeJSONStringify(this._store);
    }
  }

  /**
   * Provides a utility which allows for determining if automatic change detection
   * is of User Preferences API is enabled. When enabled, this feature can be used
   * to determine if there are changes which haven't been persisted via related
   * Auto Save APIs.
   *
   * By default, the auto change detection feature is opt-in, thus when initializing
   * the User Preferences API, to enable auto change detection, `enableChangeDetection`
   * should be set to `true`.
   *
   * ```typescript
   *  if (UserPreferences.hasChangeDetection()) {
   *    ...
   *  }
   * ```
   * @returns `true` if the change detection feature is enabled, otherwise, returns
   *          `false` (default).
   */
  hasChangeDetection(): boolean {
    return this._enableChangeDetection;
  }

  /**
   * Provides a utility which allows for determining if the the user preferences have
   * changed since they were first loaded, or last saved. This feature is only enabled
   * when the `enableChangeDetection` argument is set to `true` during initialization.
   *
   * By default, the `enableChangeDetection` feature is opt-in, thus when initializing
   * the User Preferences API, to enable change detection, the `enableChangeDetection`
   * argument is to be set to `true`.
   *
   * If the `enableChangeDetection` feature is disabled (default), this method will
   * always return `true`, otherwise, it will return `true` if the in-memory preferences
   * have changed since the preferences have been saved to the external persistence layer.
   *
   * Do to the inter-dependency between the change detection feature and the `hasChanges`
   * implementation, clients should first check to see if the change detection is enabled
   * via the `hasChangeDetection` method before relying on this method:
   *
   * ```typescript
   *  // if change detection is enabled, proceed to determine if there are unsaved changes ...
   *  if (UserPreferences.hasChangeDetection()) {
   *    // if there are unsaved changes, handle as needed ...
   *    if (UserPreferences.hasChanges()) {
   *      ...
   *    }
   *  }
   *
   *  // with the strict argument set to true, this is technically short-hand for the above ...
   *  if (UserPreferences.hasChanges(true)) {
   *    ...
   *  }
   * ```
   *
   * @param   strict When `true`, indicates that the check for change detection is
   *          is to be by-passed, and the function is to determine if there are
   *          changes and return `true` or `false` based on whether or not there
   *          are currently unsaved changes.
   *          This argument essentially provides a convenience which allows clients
   *          to invoke this function exclusively for determining if there are pending
   *          changes without the need to first check to see if change detection is
   *          enabled. Defaults to `false`.
   * @returns If the `enableChangeDetection` feature is enabled, returns `true` if
   *          the preferences have changed since they were last saved to the external
   *          persistence layer; otherwise, this method will always return `true`.
   */
  hasChanges(strict = false): boolean {
    if (this._enableChangeDetection || strict) {
      if (this._serializedStore) {
        return safeJSONStringify(this._store) !== this._serializedStore;
      }
      return false;
    }
    return true;
  }

  /**
   * The setLanguage preference provides an API for setting or resetting the
   * User's language preference.
   *
   * By default, if setLanguage is called without any arguments, the current
   * CTC persisted language preference will be read and set to localStorage
   * if the language set in localStorage is different than that which has been
   * persisted to CTC. In all cases, moment.js is set to the current language.
   *
   * @param  language The user's language preference.
   */
  setLanguage = (lng: string, isGroupDefault = false): boolean => {
    const key = Preference.defaultLanguage;
    if (!lng) {
      const language = this.get(key);
      const local    = getLocalStorage(key);
      if (!language) {
        if (language !== local) {
          moment.locale(language);
          setLocalStorage(key, language);
          return true;
        }
      } else if (local) {
        moment.locale(getLanguage());
        deleteLocalStorage(key);
        return true;
      }
    } else {
      // only update the user extended attributes if the
      // language is not group default
      !isGroupDefault && (this.setDefaultLanguage(lng));
      moment.locale(lng);
      setLocalStorage(key, lng);
    }
    return false;
  };

  /**
   * Removes the User's 'defaultLanguage' Preferences from CTC and localStorage,
   * effectively resetting the application's default language back to the browser
   * and iOn default.
   *
   * @method resetLanguage
   */
  resetLanguage = (): void => {
    this.setDefaultLanguage();
    deleteLocalStorage(Preference.defaultLanguage);
  };

  /**
   * Removes all previously persisted grid settings by setting persistGridLayouts
   * to false, as well as removing all grid columns, filters, and sorts preferences.
   *
   * @method removeAllGridSettings
   */
  removeAllGridSettings = (): void => {
    const preferences = this._resolve();
    Object.keys(preferences).forEach(preference => {
      if (preference.indexOf('grid.columns') > -1) {
        delete preferences[preference];
      }
    });
    this.set('persistGridLayouts', false);
  };

  /**
   * Returns a cloned instance of the underlying store. This function is primarily intended
   * for edge cases where the preferences store should be saved independently from the
   * UserPreferences API, such as when editing the current user, in which case a single
   * service invocation should be made.
   */
  getStore(): PreferencesStore {
    return clone(this._store);
  }

  /**
   * @private
   *
   * Provides a utility which resolves a preferences path to its given namespace (if any).
   *
   * @param   {boolean} omitNamespace When true, indicates that the preference is to be
   *          referenced from the root of the preferences store, as opposed to the namespace
   *          within the store, which is the default behavior when the UserPreferences API
   *          is initialized against a namespace.
   */
  _resolve(omitNamespace = false, provideDefault = true): PreferencesStore {
    let preferences;
    const {_store, _namespace} = this;
    if (_namespace && !omitNamespace) {
      preferences = _store[_namespace] || _store?.preferences?.[_namespace];
    } else {
      preferences = _store?.preferences || _store;
    }
    return provideDefault ? preferences || {} : preferences;
  }

  /**
   * Sets the language currently preferred by the user in preferences store.
   *
   * If the language is supported in all iOn applications then it is set at
   * the root level in preferences store else if the language is supported
   * only in a specific iOn application then it is set specific to that
   * application within the preferences store using the appropriate namespace.
   *
   * If the lng parameter isn't passed, it is assumed that user has set the
   * preference as browser default and the preference key 'defaultLanguage'
   * is deleted from the preferences store.
   *
   * @param lng currently preferred language
   */
  setDefaultLanguage = (lng = '') => {
    const key = Preference.defaultLanguage;
    if (lng) {
      if (this._initialized) {
        const omitNameSpace = Object.keys(Language)?.findIndex((lang) => lang === lng) !== -1;
        const preferences = this._resolve(omitNameSpace);
        preferences[key] = lng;
        info(`[User Preferences]: Added '${key}' User Preference.`);
      } else {
        error(`[User Preferences]: Unable to set User Preferences for ${key}`);
      }
    } else {
      this.remove(key);
      this.remove(key, false, true);
    }
  };

  /**
   * Gets the default language as preferred by the user.
   *
   * If the 'defaultLanguage' is specific to that iOn application, it is returned from that
   * specific level at the preferences store using the appropriate namespace else if it is a
   * common language supported by all iOn appications, then the 'defaultLanguage' is returned
   * from the root level of preferences store.
   */
  getDefaultLanguage = () => {
    const preferedLng = this.get(Preference.defaultLanguage);
    return Object.keys(Language)?.find(lang => lang === preferedLng);
  };

  /**
   * Persists the current preferred langauge ('defaultLanguage') in local storage.
   */
  persistDefaultLanguage = (groupDefaultLanguage?: string) => {
    const defaultLanguage = this.getDefaultLanguage();
    if (!isUndefined(defaultLanguage)) {
      this.setLanguage(defaultLanguage);
    } else if (groupDefaultLanguage) {
      this.setLanguage(getLanguageKey(groupDefaultLanguage), true);
    } else {
      this.resetLanguage();
    }
  };

  // method returns the time format from user preference...
  getSystemDateFormat = (): string => {
    return this.get(Preference.defaultDateFormat) || getDateFormat() || getDefaultDateFormat(getEnvironment());
  };

  /**
   * Helper function for detecting missed notification preferences
   * and assigning them to User.
   */
  _notificationPreferenceRequired = (preference: Preference): boolean => {
    if (isUndefined(this.get(preference))) {
      this.set(preference, false);
      return true;
    }
    return false;
  };

  /**
   * Helper function for enabling Urgent notification preferences,
   * if current user Group has them enabled.
   */
  _enableUrgentNotifications = (preference: Preference) => {
    if (!this.get(preference)) {
      this.set(preference, true);
    }
  };

  /**
  * Persists default User's notification preferences if not defined yet.
  */
  persistDefaultNotificationsPreferences = async(accountId: number) => {
    if (!this._notificationPreferencesInitialized) {
      await GroupProperties.getInheritedProperties(accountId);
      const isSaveRequired = [
        Preference.showNotifications,
        Preference.showUrgentNotifications,
        Preference.showGeneralNotifications,
        Preference.showTimeRelatedNotifications,
        Preference.showConnectivityNotifications,
        Preference.showScheduledEventsNotifications
      ].filter(this._notificationPreferenceRequired).length > 0;
      if (isSaveRequired) {
        this.save();
      }
      if (getUrgentNotificationsEnabled()) {
        [
          Preference.showNotifications,
          Preference.showUrgentNotifications
        ].forEach(preference => {
          this._enableUrgentNotifications(preference);
        });
        this.save();
      }
      this._notificationPreferencesInitialized = true;
    }
  };

  // method returns the time format from user preference...
  getSystemTimeFormat = (): string => {
    return this.get(Preference.defaultTimeFormat) || getGroupTimeFormat() || DEFAULT_TIME_FORMAT;
  };

  /**
   * @private
   *
   * **Important Note**: This method should generally never be needed and is to
   * be used with caution.
   *
   * Provides a mechanism which allows for re-initializing the UserPreferences API.
   */
  __reinitialize() {
    this._initialized = false;
    this._store = undefined;
    this._userId = undefined;
    this._namespace = undefined;
    this._readOnly = undefined;
    this._serializedStore = undefined;
    this._version = 0;
  }
}
export default new UserPreferences();
